// src/components/home/OutdoorActivity.js

import React, { useState } from "react";
import moment from "moment";
import { useAtom } from "jotai";
import { X, ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { activeChildDetail } from "../../atomstore/store";
import { useRequestProcessor } from "../../requestProcessor";
import SpinnerLoading from "./SpinnerLoading";
import { eventAction, fetchOutdoorActivity } from "../../queryfunctions/queryfunctions";

const imageUrls = [
    "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSYRy5KxOqyoXyL91IL1E1k_tQCf78ZQFudtA&s",
    "https://dayswithgrey.com/wp-content/uploads/2022/10/outdoor-activities-for-kids-109.jpg",
    "https://elfapreschools.com/singapore/wp-content/uploads/2022/04/Outdoor-relay-allows-children-to-soak-up-the-sun-and-expand-locomotor-skills-600x450.jpeg",
    "https://dayswithgrey.com/wp-content/uploads/2022/10/outdoor-activities-for-kids-113.jpg",
];

const OutdoorActivity = () => {
    const navigate = useNavigate();
    const { useQueryWrapper, useMutationWrapper } = useRequestProcessor();
    const [activeChild] = useAtom(activeChildDetail);
    const [selectedItem, setSelectedItem] = useState(null);

    const { data: activities, isLoading, refetch } = useQueryWrapper(
        ["outdoorActivity", "out" + activeChild?.id],
        () => fetchOutdoorActivity(activeChild),
        {}
    );




    const eventStatusMutation = useMutationWrapper(
        "eventAction",
        ({ event_id, status }) => eventAction(event_id, status),
        {
            onSuccess: () => refetch(),
        }
    );

    return (
        <div className=" pb-20  fixed inset-0 bg-gray-50 z-50 overflow-y-auto">

            {/* Header */}
            <div className="sticky top-0 bg-white border-b p-4 flex items-center space-x-3 z-20">
                <button onClick={() => navigate(-1)}>
                    <ChevronLeft className="w-6 h-6 text-gray-600" />
                </button>
                <h2 className="text-lg font-bold text-gray-800 cursor-pointer" onClick={() => navigate(-1)}>Outdoor Activity</h2>
            </div>

            {isLoading && <SpinnerLoading />}

            {!isLoading && (!activities || activities.length === 0) && (
                <div className="flex flex-col items-center justify-center py-16 text-center text-gray-600">
                    <p className="text-base font-semibold">No Outdoor Activities Found</p>
                    <p className="text-xs text-gray-500 mt-1">No activities have been scheduled.</p>
                </div>
            )}

            {Array.isArray(activities) &&
                activities.map((item, index) => {
                    const img = imageUrls[index % imageUrls.length];

                    return (
                        <div
                            key={index}
                            onClick={() => setSelectedItem({ ...item, image: img })}
                            className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200
                                       hover:shadow-lg hover:-translate-y-1 transition-all duration-200 cursor-pointer m-2"
                        >
                            <div className="flex items-start justify-between space-x-4">

                                {/* Image */}
                                <div className="w-20 h-20 rounded-xl overflow-hidden shadow-sm flex-shrink-0">
                                    <img
                                        src={img}
                                        className="w-full h-full object-cover"
                                        alt="Outdoor Activity"
                                    />
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-base font-semibold text-gray-900 line-clamp-1 break-words">
                                        {item.event_title}
                                    </h3>

                                    <p className="text-sm text-gray-600 mt-1 line-clamp-2 break-words">
                                        {item.event_details}
                                    </p>
                                </div>

                                {/* Status & Date */}
                                <div className="flex flex-col items-end justify-start space-y-1 min-w-[90px] flex-shrink-0">
                                    <span
                                        className={`text-xs font-semibold px-3 py-1 rounded-full shadow-sm text-white
                                            ${item.consent_status === "Authorized"
                                                ? "bg-green-500"
                                                : item.consent_status === "Denied"
                                                    ? "bg-red-500"
                                                    : "bg-yellow-500"
                                            }
                                        `}
                                    >
                                        {item.consent_status || "Pending"}
                                    </span>

                                    <span className="text-[10px] text-gray-500 whitespace-nowrap">
                                        {moment(item.event_date).format("ll")}
                                    </span>
                                </div>
                            </div>
                        </div>
                    );
                })}

            {/* Modal */}
            {selectedItem && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-md rounded-2xl shadow-xl overflow-hidden animate-fadeIn flex flex-col max-h-[85vh]">

                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-cyan-500 to-blue-600 p-4 flex justify-between items-center">
                            <h3 className="text-white font-semibold text-lg">
                                {selectedItem.event_title}
                            </h3>
                            <button onClick={() => setSelectedItem(null)}>
                                <X className="w-6 h-6 text-white" />
                            </button>
                        </div>

                        <div className="overflow-y-auto">
                            {/* Image Preview */}
                            <div className="w-full h-48 overflow-hidden">
                                <img
                                    src={selectedItem.image}
                                    className="w-full h-full object-cover"
                                    alt="Outdoor Activity"
                                />
                            </div>

                            {/* Body */}
                            <div className="p-4 space-y-3">
                                <p className="text-sm text-gray-700">
                                    <span className="font-semibold">Event Date:</span>{" "}
                                    {moment(selectedItem.event_date).format("ll")}
                                </p>

                                <p className="text-sm text-gray-700">
                                    <span className="font-semibold">Created On:</span>{" "}
                                    {moment(selectedItem.event_created_on).format("ll")}
                                </p>

                                <p className="text-sm text-gray-700 break-words">
                                    <span className="font-semibold">Details:</span>{" "}
                                    {selectedItem.event_details}
                                </p>
                            </div>
                        </div>

                        {/* Buttons */}
                        <div className="p-4 flex justify-end space-x-3 bg-gray-50">

                            {(moment().isBefore(selectedItem.event_date) ||
                                moment().format("YYYY-MM-DD") === selectedItem.event_date) &&
                                selectedItem.event_status !== "Cancelled" && (
                                    <>
                                        {selectedItem.consent_status !== "Authorized" && (
                                            <button
                                                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm"
                                                onClick={() => {
                                                    eventStatusMutation.mutate({
                                                        event_id: selectedItem.event_id,
                                                        status: "Authorized",
                                                    });
                                                    setSelectedItem(null);
                                                }}
                                            >
                                                Authorise
                                            </button>
                                        )}

                                        {selectedItem.consent_status !== "Denied" && (
                                            <button
                                                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm"
                                                onClick={() => {
                                                    eventStatusMutation.mutate({
                                                        event_id: selectedItem.event_id,
                                                        status: "Denied",
                                                    });
                                                    setSelectedItem(null);
                                                }}
                                            >
                                                Deny
                                            </button>
                                        )}
                                    </>
                                )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OutdoorActivity;
